import { useMemo } from 'react';
import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { em, NavLink } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { Acl } from '@/components/acl';
import type { icons } from '@/components/icons/icons';
import { LucideIcon } from '@/components/icons/LucideIcons';
import { useAppStore } from '@/stores';
import classNames from './NavbarLink.module.css';

export type NavbarLinkProps = {
  label: string;
  icon?: keyof typeof icons;
  to?: string;
  permissions?: Array<string>;
  children?: Array<NavbarLinkProps>;
  className?: string;
};

const NavbarLink = ({ to, icon, label, children, className, permissions }: NavbarLinkProps) => {
  const sideBarToggle = useAppStore((s) => s.sideBarToggle);
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const getChildPermissions = useMemo(() => {
    if (Array.isArray(children)) {
      return children.map(({ permissions: p }) => p)?.flat();
    }
    return [];
  }, [children]);

  const [opened, { toggle }] = useDisclosure(!!matchRoute({ to: to, fuzzy: true }));
  const isMobile = useMediaQuery(`(max-width: ${em(750)})`);
  return (
    <Acl permissions={[...(permissions ?? []), ...(getChildPermissions ?? [])]}>
      <NavLink
        opened={opened}
        onClick={(e) => {
          if (Array.isArray(children) && children.length > 0) {
            e.preventDefault(); // prevent navigation
            toggle();
          } else {
            if (isMobile) {
              sideBarToggle(); // close sidebar on leaf click
            }
            navigate({ to });
          }
        }}
        color="primary"
        component={Link}
        to={to}
        leftSection={icon && <LucideIcon name={icon} />}
        label={label}
        h={48}
        variant="filled"
        classNames={classNames}
        className={clsx(className)}
        // defaultOpened={!!matchRoute({ to: to, fuzzy: true })}
      >
        {Array.isArray(children)
          ? (Array.isArray(children) ? children : []).map((link) => (
              <NavbarLink key={link.to} {...link} className={classNames.child} />
            ))
          : null}
      </NavLink>
    </Acl>
  );
};

export default NavbarLink;
